import React, { useState } from "react";
import { Box, Paper, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import { UnfoldLess, UnfoldMore } from "@mui/icons-material";
import PopUpMenu from "../../PopUpMenu/PopUpMenu";
import MenuItem from "@mui/material/MenuItem";
import LegalbirdIoConfirm from "../../Modal/LegalbirdIoConfirm";
import { useMutation } from "@tanstack/react-query";
import { updateResource } from "../../../services/ReactQuery/reactQueryService";
import RequestSaveNoteModal from "./RequestSaveNoteModal";
import { Note as NoteType } from "../../../types/Note";
import noteStyles from "./NoteStyles";
import { apiGet } from "../../../services/Api/apiCall";
import { enqueueSnackbar } from "notistack";
import { useDrawer } from "../../../provider/DrawerProvider";
import { ProductUnion } from "../../../types/ProductUnion";
import dayjs from "dayjs";

type NoteProps = {
  note: NoteType;
  handleChange?: Function;
  startsCollapsed?: boolean;
  product?: ProductUnion;
  isEditable?: boolean;
  authorNameOverride?: string;
};
const Note = ({
  note,
  handleChange = () => {},
  startsCollapsed = false,
  product,
  isEditable = true,
  authorNameOverride,
}: NoteProps) => {
  const updateMutation = useMutation({
    mutationFn: updateResource
  });
  const [noteFolded, setNoteFolded] = useState(startsCollapsed);
  const [deleteNoteOpen, setDeleteNoteOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const { setDrawerState } = useDrawer();

  const unpinNote = async () => {
    if (!note.id) {
      return;
    }
    await updateMutation.mutateAsync({ id: note.id, uri: "notes", data: { pinned: false } });
    handleChange();
  };

  const pinNote = async () => {
    if (!note.id || !product) {
      return;
    }
    const pinnedNotes = await apiGet(`/notes?case=${product.backofficeCase["@id"]}&pinned=true&deleted=false`);

    if (pinnedNotes["hydra:totalItems"] >= 4) {
      enqueueSnackbar(
        "Es ist bereits die maximal mögliche Anzahl an Notizen am Fall angeheftet. Bitte vorhandene Notizen abheften oder aktualisieren. Sobald weniger als 4 Notizen am Fall sind, kann eine neue Notiz angelegt werden.",
        {
          variant: "error",
          isNonInteractive: true,
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        }
      );
    } else {
      await updateMutation.mutateAsync({ id: note.id, uri: "notes", data: { pinned: true } });
      handleChange();
    }
  };

  const deleteNote = async () => {
    if (!note.id) {
      return;
    }
    await updateMutation.mutateAsync({ id: note.id, uri: "notes", data: { deleted: true, pinned: false } });
    handleChange();
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Paper sx={[noteStyles.paper, note.isWarning ? noteStyles.warning : null]}>
      <Grid container>
        <Grid sx={noteStyles.info}>
          {authorNameOverride || note.author?.person.fullname || "System"} | {dayjs(note.created).format("DD.MM.YYYY")}{" "}
          | {dayjs(note.created).format("HH:mm")}
        </Grid>
        {note.content &&
          (note.content.length > 200 ||
            note.content.includes("<br>") ||
            note.content.includes("<br/>") ||
            (note.content.match(/<p\b[^>]*>/g) || []).length > 1) && (
            <Grid>
              <IconButton onClick={() => setNoteFolded(!noteFolded)} sx={noteStyles.icon} size="large">
                {noteFolded ? <UnfoldMore /> : <UnfoldLess />}
              </IconButton>
            </Grid>
          )}
        {isEditable && (
          <Grid>
            <PopUpMenu>
              <MenuItem onClick={() => setDrawerState({ type: "note", entityData: note })}>Bearbeiten</MenuItem>
              <MenuItem onClick={note.pinned ? unpinNote : pinNote}>{note.pinned ? "Losheften" : "Anheften"}</MenuItem>
              <MenuItem onClick={() => setOpen(true)}>Als Dokument speichern</MenuItem>
              <MenuItem onClick={() => setDeleteNoteOpen(true)}>Löschen</MenuItem>
            </PopUpMenu>
            <LegalbirdIoConfirm
              handleClose={() => setDeleteNoteOpen(false)}
              open={deleteNoteOpen}
              handleConfirm={deleteNote}
              content="Möchten Sie diese Notiz tatsächlich löschen? Dies kann nicht rückgängig gemacht werden."
              confirmText="Notiz löschen"
              headline="Notiz löschen?"
            />
          </Grid>
        )}
      </Grid>
      {open && note.content && !!product && (
        <RequestSaveNoteModal product={product} note={note.content} handleClose={handleClose} open={open} />
      )}
      <Box
        sx={[noteStyles.note, noteFolded ? noteStyles.collapsedNote : null]}
        dangerouslySetInnerHTML={{ __html: note.content! }}
      />
      {note.deleted && <Box sx={noteStyles.overlay} />}
      {note.lastTouchedBy && (
        <Box sx={noteStyles.lastChange}>
          {(note.deleted ? "Gelöscht durch " : "Zuletzt geändert durch ") +
            note.lastTouchedBy.person.fullname +
            " | " +
            dayjs(note.updated).format("DD.MM.YYYY | HH:mm")}
        </Box>
      )}
    </Paper>
  );
};

export default Note;
